import { CheckCircle2, MessageSquare, Smile, Users } from "lucide-react";
import SiteLayout from "./components/layout/SiteLayout";
import { focusRing } from "./components/ui/focusRing";

function Docs() {
  return (
    <SiteLayout navVariant="inner" footerVariant="docs">
      <main className="pt-32 pb-20 px-4 relative">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-stone-800 mb-2">Documentation</h1>
          <p className="text-stone-500 mb-12">Everything you need to know about using Kyubot</p>

          <div className="prose prose-stone max-w-none prose-headings:font-bold prose-a:text-brand-orange prose-a:no-underline hover:prose-a:text-brand-orange-hover">
            {/* Quick Start */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Quick Start</h2>
              </div>
              <p className="text-stone-600 leading-relaxed mb-4">
                Kyubot works in any Slack channel where it's installed. There are two ways to add
                items to your queue:
              </p>
              <div className="bg-white rounded-xl border border-amber-100 p-6 mb-4">
                <h3 className="text-lg font-semibold text-stone-800 mb-3">
                  Option 1: Use a command
                </h3>
                <p className="text-stone-600 mb-2">
                  Type <code className="bg-slate-100 px-2 py-0.5 rounded text-amber-700">!add</code>{" "}
                  followed by your item:
                </p>
                <div className="bg-slate-50 rounded-lg p-3 font-mono text-sm text-stone-700">
                  !add Review the Q4 budget proposal
                </div>
              </div>
              <div className="bg-white rounded-xl border border-amber-100 p-6">
                <h3 className="text-lg font-semibold text-stone-800 mb-3">
                  Option 2: React with an emoji
                </h3>
                <p className="text-stone-600">
                  React to any message with 🎟️ (admission ticket) and Kyubot will add that message to
                  the queue automatically.
                </p>
                <p className="text-stone-600">
                  React with 🙋 (raising hand) to take a ticket (assign it to yourself).
                </p>
              </div>
            </section>

            {/* Commands */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Commands</h2>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !add &lt;item&gt;
                    </code>
                    <p className="text-stone-600 m-0">
                      Add an item to the queue. The item text can be anything you want to track.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !list
                    </code>
                    <p className="text-stone-600 m-0">
                      Show all items currently in the queue, with who added them and when.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !done
                    </code>
                    <p className="text-stone-600 m-0">
                      Mark the first item in the queue as done. The person who added it gets
                      notified.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !done &lt;n|text&gt;
                    </code>
                    <p className="text-stone-600 m-0">
                      Mark a specific item as done by its number (e.g.,{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !done 3
                      </code>
                      ) or by matching text (e.g.,{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !done budget
                      </code>
                      ).
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !take
                    </code>
                    <p className="text-stone-600 m-0">
                      Assign the current thread's item (or first unassigned item) to yourself.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !assign @user
                    </code>
                    <p className="text-stone-600 m-0">
                      Assign the current thread's item (or first unassigned item) to someone else.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !help
                    </code>
                    <p className="text-stone-600 m-0">Show a quick reference of all commands.</p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-stone-800 mt-8 mb-4 flex items-center gap-2">
                <span className="bg-gradient-to-r from-brand-orange to-brand-orange-hover text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  PRO
                </span>
                Pro Commands
              </h3>

              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-brand-orange/30 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !due &lt;time&gt;
                    </code>
                    <p className="text-stone-600 m-0">
                      Set a due date on the current thread's item. Examples:{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !due 2h
                      </code>
                      ,{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !due 1d
                      </code>
                      ,{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !due 1w
                      </code>
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-brand-orange/30 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !stats
                    </code>
                    <p className="text-stone-600 m-0">
                      View queue statistics including items completed, average time to close, and
                      top contributors. Defaults to last week.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-brand-orange/30 p-5">
                  <div className="flex items-start gap-4">
                    <code className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-mono text-sm whitespace-nowrap">
                      !export &lt;range&gt;
                    </code>
                    <p className="text-stone-600 m-0">
                      Export a CSV report of your queue activity, which shows item status, who added
                      and completed each item, timestamps, resolution time, and a link
                      back to the original message. Examples:{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !export 1w
                      </code>
                      ,{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !export ytd
                      </code>
                      ,{" "}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                        !export 01/01-01/31
                      </code>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Emoji Reactions */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 text-pink-600 rounded-xl flex items-center justify-center">
                  <Smile className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Emoji Reactions</h2>
              </div>
              <p className="text-stone-600 leading-relaxed mb-4">
                Kyubot responds to emoji reactions, making it easy to manage your queue without
                typing commands:
              </p>

              <div className="grid gap-4">
                <div className="bg-white rounded-xl border border-amber-100 p-5 flex items-center gap-4">
                  <span className="text-3xl">🎟️</span>
                  <div>
                    <h3 className="font-semibold text-stone-800 m-0">Add to queue</h3>
                    <p className="text-stone-600 text-sm m-0">
                      React to any message with the ticket emoji to add it to the queue
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5 flex items-center gap-4">
                  <span className="text-3xl">🙋</span>
                  <div>
                    <h3 className="font-semibold text-stone-800 m-0">Take a ticket</h3>
                    <p className="text-stone-600 text-sm m-0">
                      React with 🙋 to assign the ticket to yourself
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-amber-100 p-5 flex items-center gap-4">
                  <span className="text-3xl">✅</span>
                  <div>
                    <h3 className="font-semibold text-stone-800 m-0">Mark as done</h3>
                    <p className="text-stone-600 text-sm m-0">
                      React to a queued message with the checkmark to mark it complete
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-stone-500 text-sm mt-4">
                Tip: Remove your 🎟️ reaction to remove an item from the queue before it's completed.
              </p>
            </section>

            {/* Workflow Example */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Example Workflow</h2>
              </div>

              <div className="bg-white rounded-xl border border-amber-100 p-6">
                <ol className="space-y-4 m-0 p-0 list-none">
                  <li className="flex gap-4">
                    <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      1
                    </span>
                    <div>
                      <p className="font-medium text-stone-800 m-0">Someone asks for help</p>
                      <p className="text-stone-500 text-sm m-0 mt-1">
                        "Can someone reset my VPN credentials?"
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      2
                    </span>
                    <div>
                      <p className="font-medium text-stone-800 m-0">
                        React with 🎟️ to add it to the queue
                      </p>
                      <p className="text-stone-500 text-sm m-0 mt-1">
                        Kyubot confirms: "Added to queue"
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      3
                    </span>
                    <div>
                      <p className="font-medium text-stone-800 m-0">Assign it to yourself</p>
                      <p className="text-stone-500 text-sm m-0 mt-1">
                        Type{" "}
                        <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                          !take
                        </code>{" "}
                        in the thread
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      4
                    </span>
                    <div>
                      <p className="font-medium text-stone-800 m-0">
                        Complete the task and mark it done
                      </p>
                      <p className="text-stone-500 text-sm m-0 mt-1">
                        Type{" "}
                        <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                          !done
                        </code>{" "}
                        — the requester gets notified automatically
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </section>

            {/* Plans & Limits */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 text-green-600 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Plans & Limits</h2>
              </div>
              <p className="text-stone-600 leading-relaxed mb-6">
                Kyubot offers a free tier for small teams and a Pro plan for growing organizations.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="text-lg font-bold text-stone-800 mb-2">Free Plan</h3>
                  <p className="text-stone-500 mb-4 text-sm">Perfect for testing and small teams</p>
                  <ul className="space-y-2 text-sm text-stone-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-stone-400" />
                      <span>1 active queue</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-stone-400" />
                      <span>Up to 10 items in queue</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-stone-400" />
                      <span>Basic support</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl border border-brand-orange/30 p-6 relative overflow-hidden">
                  <h3 className="text-lg font-bold text-stone-800 mb-2">Pro Plan</h3>
                  <p className="text-stone-500 mb-4 text-sm">For power users and larger teams</p>
                  <ul className="space-y-2 text-sm text-stone-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                      <span>5 active queues</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                      <span>Up to 50 items per queue</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                      <span>Due dates & overdue tracking</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                      <span>Stats & CSV export</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Upgrading */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Upgrading</h2>
              </div>
              <p className="text-stone-600 leading-relaxed mb-4">
                Ready to unlock more queues and capacity? You can upgrade directly from Slack.
              </p>

              <div className="bg-white rounded-xl border border-amber-100 p-6">
                <h3 className="text-lg font-semibold text-stone-800 mb-3">How to Upgrade</h3>
                <ol className="space-y-4 m-0 p-0 list-none">
                  <li className="flex gap-4">
                    <span className="w-6 h-6 bg-slate-100 text-stone-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      1
                    </span>
                    <div>
                      <p className="text-stone-800 m-0">
                        Type{" "}
                        <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                          !upgrade
                        </code>{" "}
                        in any channel where Kyubot is invited.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-6 h-6 bg-slate-100 text-stone-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      2
                    </span>
                    <div>
                      <p className="text-stone-800 m-0">
                        Kyubot will send you a secure link to complete your purchase via Stripe.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-6 h-6 bg-slate-100 text-stone-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      3
                    </span>
                    <div>
                      <p className="text-stone-800 m-0">
                        Once completed, your workspace will be automatically upgraded.
                      </p>
                    </div>
                  </li>
                </ol>
                <div className="mt-6 pt-6 border-t border-stone-100">
                  <p className="text-stone-600 text-sm m-0">
                    <strong className="text-stone-800">Want to try before you buy?</strong>
                    <br />
                    Type{" "}
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                      !trial
                    </code>{" "}
                    to start a 30-day free trial of the Pro plan. No credit card required.
                  </p>
                </div>
              </div>
            </section>

            {/* Tips */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-stone-800 m-0">Tips for Teams</h2>
              </div>

              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-stone-600">
                    <strong>Use threads:</strong> Reply in threads to keep conversations organized.
                    Commands work in threads too!
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-stone-600">
                    <strong>Check the queue:</strong> Run{" "}
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">!list</code>{" "}
                    regularly to see what's pending.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-stone-600">
                    <strong>Assign work:</strong> Use{" "}
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">!take</code>{" "}
                    or{" "}
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-amber-700">
                      !assign
                    </code>{" "}
                    so everyone knows who's handling what.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-stone-600">
                    <strong>Close the loop:</strong> Always mark items done so requesters know their
                    request was handled.
                  </span>
                </li>
              </ul>
            </section>

            {/* Need Help */}
            <section className="bg-gradient-to-br from-brand-orange to-brand-orange-hover text-stone-900 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-2 text-white">Need Help?</h2>
              <p className="text-white mb-4">
                Have questions or feedback? We'd love to hear from you.
              </p>
              <a
                href="mailto:support@kyubot.com"
                className={`inline-block bg-white text-stone-900 px-6 py-2 rounded-full font-semibold hover:bg-orange-50 transition-colors ${focusRing}`}
              >
                Contact Support
              </a>
            </section>
          </div>
        </div>
      </main>
    </SiteLayout>
  );
}

export default Docs;
